import React from 'react';
import styled from 'styled-components';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/common/SEO';
import { FaCrosshairs, FaShieldAlt, FaChartBar, FaArrowRight } from 'react-icons/fa';
import theme from '../../theme';

const PageContainer = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  position: relative;
  padding: 120px 5% 80px;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, ${theme.colors.primary} 100%);
  text-align: center;
  color: white;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: -50%;
    right: -20%;
    width: 700px;
    height: 700px;
    background: radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 750px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (max-width: ${theme.breakpoints.tablet}) { font-size: 2.2rem; }
  @media (max-width: ${theme.breakpoints.mobile}) { font-size: 1.75rem; }
`;

const IntroSection = styled.section`
  padding: 70px 5%;
  background: white;
`;

const IntroContainer = styled.div`
  max-width: 780px;
  margin: 0 auto;
`;

const IntroText = styled.p`
  font-size: 1.05rem;
  color: ${theme.colors.secondary};
  line-height: 1.85;
  margin-bottom: 1.75rem;

  &:last-child { margin-bottom: 0; }
`;

const SectionBlock = styled.section`
  padding: 70px 5%;
  background: ${props => props.$gray ? theme.colors.lightGray : 'white'};
`;

const SectionContainer = styled.div`
  max-width: 780px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 1.6rem;
  font-weight: 800;
  color: ${theme.colors.secondary};
  margin-bottom: 1.5rem;
  line-height: 1.3;

  @media (max-width: ${theme.breakpoints.mobile}) { font-size: 1.3rem; }
`;

const SectionText = styled.p`
  font-size: 1rem;
  color: ${theme.colors.darkGray};
  line-height: 1.8;
  margin-bottom: 1rem;

  &:last-child { margin-bottom: 0; }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.95rem;
  color: ${theme.colors.secondary};
  line-height: 1.5;

  &:before {
    content: '';
    flex-shrink: 0;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #8B0000;
  }
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  max-width: 900px;
  margin: 0 auto;

  @media (max-width: ${theme.breakpoints.tablet}) { grid-template-columns: 1fr; }
`;

const ValueCard = styled.div`
  background: white;
  padding: 2rem 1.75rem;
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  border: 1px solid #e5e7eb;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 30px rgba(0,0,0,0.08);
    border-color: rgba(139, 0, 0, 0.15);
  }

  svg {
    width: 32px;
    height: 32px;
    color: #8B0000;
    margin-bottom: 1rem;
  }

  h3 {
    font-size: 1.05rem;
    font-weight: 700;
    color: ${theme.colors.secondary};
    margin-bottom: 0.6rem;
  }

  p {
    font-size: 0.88rem;
    color: ${theme.colors.darkGray};
    line-height: 1.65;
  }
`;

const CTASection = styled.section`
  padding: 70px 5%;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  text-align: center;
  color: white;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: -40%;
    right: -15%;
    width: 350px;
    height: 350px;
    background: radial-gradient(circle, rgba(139,0,0,0.08) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const CTAContent = styled.div`
  position: relative;
  z-index: 1;
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2.25rem;
  background: #8B0000;
  color: white;
  font-weight: 700;
  font-size: 0.95rem;
  border-radius: 50px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(139, 0, 0, 0.3);

  &:hover {
    background: #6b0000;
    transform: translateY(-2px);
    box-shadow: 0 12px 35px rgba(139, 0, 0, 0.45);
    color: white;
  }

  svg {
    width: 14px;
    height: 14px;
    transition: transform 0.3s ease;
  }
  &:hover svg { transform: translateX(4px); }
`;

const AboutPage = () => {
  const { t } = useTranslation();
  const { lang } = useParams();

  const d = (key) => t(`about.${key}`);

  return (
    <>
      <SEO titleKey="about.seo.title" descriptionKey="about.seo.description" />

      <PageContainer>
        <HeroSection>
          <HeroContent>
            <HeroTitle>{d('hero.title')}</HeroTitle>
          </HeroContent>
        </HeroSection>

        <IntroSection>
          <IntroContainer>
            <IntroText>{d('intro.p1')}</IntroText>
            <IntroText>{d('intro.p2')}</IntroText>
            <IntroText>{d('intro.p3')}</IntroText>
          </IntroContainer>
        </IntroSection>

        <SectionBlock $gray>
          <SectionContainer>
            <SectionTitle>{d('platform.title')}</SectionTitle>
            <SectionText>{d('platform.intro')}</SectionText>
            <FeatureList>
              <FeatureItem>{d('platform.f1')}</FeatureItem>
              <FeatureItem>{d('platform.f2')}</FeatureItem>
              <FeatureItem>{d('platform.f3')}</FeatureItem>
              <FeatureItem>{d('platform.f4')}</FeatureItem>
              <FeatureItem>{d('platform.f5')}</FeatureItem>
            </FeatureList>
            <SectionText style={{ marginTop: '1.25rem' }}>{d('platform.note')}</SectionText>
          </SectionContainer>
        </SectionBlock>

        <SectionBlock>
          <SectionContainer>
            <SectionTitle>{d('europe.title')}</SectionTitle>
            <SectionText>{d('europe.intro')}</SectionText>
            <FeatureList>
              <FeatureItem>{d('europe.f1')}</FeatureItem>
              <FeatureItem>{d('europe.f2')}</FeatureItem>
              <FeatureItem>{d('europe.f3')}</FeatureItem>
              <FeatureItem>{d('europe.f4')}</FeatureItem>
            </FeatureList>
            <SectionText style={{ marginTop: '1.25rem' }}>{d('europe.note')}</SectionText>
          </SectionContainer>
        </SectionBlock>

        <SectionBlock $gray>
          <SectionContainer>
            <SectionTitle>{d('approach.title')}</SectionTitle>
            <ValuesGrid>
              <ValueCard>
                <FaCrosshairs />
                <h3>{d('approach.v1.title')}</h3>
                <p>{d('approach.v1.text')}</p>
              </ValueCard>
              <ValueCard>
                <FaShieldAlt />
                <h3>{d('approach.v2.title')}</h3>
                <p>{d('approach.v2.text')}</p>
              </ValueCard>
              <ValueCard>
                <FaChartBar />
                <h3>{d('approach.v3.title')}</h3>
                <p>{d('approach.v3.text')}</p>
              </ValueCard>
            </ValuesGrid>
          </SectionContainer>
        </SectionBlock>

        <CTASection>
          <CTAContent>
            <CTAButton to={`/${lang}/demo`}>
              {d('cta.button')} <FaArrowRight />
            </CTAButton>
          </CTAContent>
        </CTASection>
      </PageContainer>
    </>
  );
};

export default AboutPage;
